<script setup>
import "flatpickr/dist/flatpickr.css";
import * as Sentry from "@sentry/vue";
import { defineProps, computed, ref, onMounted } from "vue";
import { useVModel } from "@vueuse/core";
import {
  COMMUNICATION,
  MOBILITY,
  SENSORY,
  DEFAULT_EXCURSION_IMAGE,
} from "@/constants";
import eventsService from "@/services/events";

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {
      return {};
    },
  },
});

const events = useVModel(props, "modelValue");

const locationStore = ref([]);
const areLocationsLoading = ref(false);

const loadLocations = async () => {
  if (locationStore.value.length === 0 && !areLocationsLoading.value) {
    areLocationsLoading.value = true;
    try {
      const {
          data: {
            data: { items },
          },
        } = await eventsService.getLocations();
      locationStore.value = items;
    } catch (error) {
      Sentry.captureException(
          error,
          {
            tags: {
              troubleshooting_note: "Failed to load locations from events service",
            },
          }
        );
    } finally {
      areLocationsLoading.value = false;
    }
  }
};

const locations = computed(() => {
  return events?.value?.id && !areLocationsLoading.value
    ? locationStore.value.filter(
        (location) => location?.event_id === events?.value?.id
      )
    : [];
});

const defaultImage = DEFAULT_EXCURSION_IMAGE;

onMounted(() => {
  loadLocations();
});
</script>
<template>
  <div class="relative mt-4">
    <div class="mt-4 text-base text-gray-700">
      Getting to the Event:
      <span v-if="events.Address" class="font-semibold text-gray-700">{{
        events.Address
      }}</span>
    </div>
    <div v-for="(location, index) in locations" :key="index" class="relative">
      <div class="font-semibold text-gray-700 mt-4">
        Location {{ index + 1 }}
      </div>
      <div class="text-base mb-4 font-medium text-gray-700">
        Address:
        <span v-if="location.address" class="font-semibold"
          >{{ location.address?.number }}
          {{ location.address?.street_address_1 }},
          {{ location.address?.city }}, {{ location.address?.state }},
          {{ location.address?.country }}</span
        >
      </div>
      <div class="text-base mb-4 font-medium text-gray-700">
        City:
        <span v-if="location.city" class="font-semibold"
          >{{ location.city?.city_ascii }},
          {{ location.city?.country?.name }}</span
        >
      </div>
      <div class="mt-4">
        Image(s):
        <div v-if="events.image">
          <img
            v-for="(image, ind) in JSON.parse(events.image)"
            :key="ind"
            :src="image ?? DEFAULT_EXCURSION_IMAGE"
            class="mt-4"
          />
        </div>
        <div v-else>
          <img :src="defaultImage" class="mt-4" />
        </div>
      </div>
      <div class="mt-4">
        Accessibility:
        <div class="mt-4">
          Mobility:
          <span v-if="location?.mobility" class="font-semibold">{{
            MOBILITY.filter((item) =>
              JSON.parse(location.mobility).includes(item.id)
            )
              .map((item) => item.name)
              .join(", ") || ""
          }}</span>
        </div>
        <div class="mt-4">
          Communication:
          <span v-if="location?.communication" class="font-semibold">{{
            COMMUNICATION.filter((item) =>
              JSON.parse(location.communication).includes(item.id)
            )
              .map((item) => item.name)
              .join(", ") || ""
          }}</span>
        </div>
        <div class="mt-4">
          Sensory:
          <span v-if="location?.sensory" class="font-semibold">{{
            SENSORY.filter((item) =>
              JSON.parse(location.sensory).includes(item.id)
            )
              .map((item) => item.name)
              .join(", ") || ""
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
